/**
 * Narcis Product Configuration
 *
 * Defines all narcis-specific settings, services, branding, and features.
 */

import type {FunnelOverrides} from '@core/products/funnel'
import {SITE_DESCRIPTION} from '@product/narcis/proposition'
import {surfaceOgImageUrl} from '@core/og/cardUrls'

export const narcisConfig = {
  id: 'narcis',
  name: 'Narcis.ai',
  domain: 'narcis.ai',
  version: '1.8',

  // SEO & Meta
  meta: {
    title: 'AI Photoshoot From Your Own Model & Face Swap | Narcis.ai',
    description: SITE_DESCRIPTION,
    url: 'https://narcis.ai',
    keywords:
      'AI portrait generator, face swap, AI portrait, face swap online, generate AI portrait, swap faces, AI photoshoot, face swap free',
    ogImage: surfaceOgImageUrl('https://narcis.ai', 'home'),
    ogImageType: 'image/png' as const,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    themeColor: '#0f0f0f',
    colorScheme: 'dark' as const,
  },

  // Backend Services — internal service addresses. Server-only: @product/narcis/config.server.ts
  // (narcisServerConfig.services). Never on this object — narcis/config.ts is imported directly by
  // client components (Footer, …) and this whole module is serialized into every page's hydration
  // script by root.tsx's loader.

  // Feature Flags
  features: {
    streamingGeneration: true,
    examples: false, // Temporarily disabled
  },

  // Narcis-specific: free face swaps for anonymous users before signup gate
  freeSwaps: 5,

  // Free officina tool uses for anonymous users before signup gate (per tool, per session)
  freeToolUses: 5,

  // Contact
  // Sign-in methods beside Google and the email link (`@core/auth/signInMethods`).
  signIn: {facebook: true, googleOneTap: true},

  contact: {
    email: 'contact@narcis.ai',
  },

  // Model version — bump when generation config changes (checkpoint, parameters)
  // 0.x = the original SDXL + face-adapter stack. 1.0 = the production Z-Image base +
  // DD face-detailer architecture (first major). 1.1 = canon turbo point (steps 9, cfg 0.0,
  // start_merge 1000, base IP-Adapter/InstantID off, identity via face_id_weight + DD).
  // 1.2 = DD standard path (differential mode, reference InstantID unconditional, crown-keep
  // crop). 1.3 = SAM3 head/hair mask standard. 1.4-20260710 = Track R "deep attenuation +
  // refine 0.2 · gs 2" — tightened Stage 1 DD knobs + a new Stage 2 img2img refine pass
  // (see generation/model_version.py history). Public string kept neutral.
  model: {
    version: '1.4-20260710',
    label: 'Narcis v1.4',
  },

  // Content compliance moderation
  compliance: {
    threshold: 0.3,
    blockedCategories: ['Porn', 'Hentai'] as const,
    errorMessage: 'This image cannot be used. Please upload an appropriate image.',
  },

  // Funnel — overrides only (defaults in core/products/funnel.ts).
  // Consumed by app/core/products/registry.ts:17 (getProductFunnel → resolveFunnel).
  // The loader in narcis/root.tsx calls getProductFunnel() on every request to
  // populate the funnel data passed to UserBlock and the header token pill.
  funnel: {
    signupBonus: 5,
    unit: {singular: 'token', plural: 'tokens'},
    cta: {purchase: 'Buy tokens'},
  } satisfies FunnelOverrides,

  // Session cookie domain is server-only: @product/narcis/config.server.ts
  // (narcisServerConfig.session), same reason as `services` above.
} as const

export type NarcisConfig = typeof narcisConfig

/** Maximum number of face photos accepted per generation request (client UX cap). */
export const MAX_FACES = 5

/**
 * Default number of concurrent streams for the render-batch (1 | 2 | 3).
 * Currently 1 because triptych UX is parked. The GPU runs out of memory when tensor-batching
 * 2+ requests (522 MB free at idle), so batch=2 falls to sequential Path B and defeats the
 * per-slot multiplexer. Flip back to 2 or 3 once the OOM is resolved (sequential VAE decode,
 * smaller intermediates, or a larger GPU instance).
 */
export const PORTRAIT_DEFAULT_TAKES: 1 | 2 | 3 = 1

/**
 * Base URL for narcis-generated images. Routes through narcis.ai/CloudFront so image:loc entries
 * in the sitemap are same-origin (covered by sc-domain:narcis.ai — no separate GSC property).
 * CloudFront serves these from the content S3 bucket via OAC SigV4; raw S3 URLs remain publicly
 * readable as a fallback for face-swap and any pre-existing consumers.
 *
 * Note: OG image transcoding (toPngDataUri in og/$slug.tsx) now fetches images via narcis.ai
 * from inside ECS — one extra CDN hop vs direct-S3. Acceptable; the image is cache-hit on
 * CloudFront after the first request.
 */
export const NARCIS_GENERATIONS_BASE_URL = 'https://narcis.ai/generations'

/**
 * Base URL for pack sample images (originals and thumbs). Prefix is `/pack-samples`.
 * Each pack slug gets its own sub-prefix: `<base>/<slug>/<id>.webp` for originals,
 * `<base>/<slug>/<id>-thumb.webp` for 256×384 thumbnails.
 * Routes through narcis.ai/CloudFront for the same GSC same-origin benefit as generations.
 * Override via `NARCIS_PACK_SAMPLES_BASE_URL` env var for dev environments pointing at a
 * local bucket or a non-production host.
 */
export const NARCIS_PACK_SAMPLES_BASE_URL =
    process.env.NARCIS_PACK_SAMPLES_BASE_URL || 'https://narcis.ai/pack-samples'

// ─── Studio free-quota constants (Wave 1 — pre-staged, not yet enforced) ─────
//
// Counter is lifetime all-time per account (not monthly, no reset) — see plan §0.
// Anonymous counter keyed on sessionId, signed-in on userId.
// The Wave 3 loader gate will read these; pre-staging here keeps the constants in
// a single source of truth instead of magic numbers scattered across loader/action files.

/** Free renders for anonymous visitors before the SignupGate appears. Lifetime, all-time. */
export const FREE_PORTRAITS_ANON = 3

/** Additional free renders granted upon signing in. Signed-in total = anon + this. Lifetime. */
export const FREE_PORTRAITS_SIGNED_IN = 3

/** Soft cap: renders/month beyond which speed degrades inside Studio (still included in sub). */
export const STUDIO_SOFT_CAP = 500
